/**
 * Extracts the domain (hostname) from a URL for use with favicon APIs and display.
 * Strips the "www." prefix when present.
 *
 * @param href - Full URL string (e.g. "https://www.cnn.com/world")
 * @returns Domain without www (e.g. "cnn.com"), or null if href is invalid
 */
export function getDomainFromUrl(href: string): string | null {
  if (!href || typeof href !== 'string') return null;
  try {
    const url = new URL(href);
    let hostname = url.hostname || '';
    if (hostname.startsWith('www.')) {
      hostname = hostname.slice(4);
    }
    return hostname || null;
  } catch {
    return null;
  }
}

/**
 * Extracts the domain from a URL for display purposes (e.g. "finance.yahoo.com").
 * Falls back to the original href if the domain cannot be parsed.
 *
 * @param href - Full URL string (e.g. "https://finance.yahoo.com/quote/AAPL")
 * @returns Domain string (e.g. "finance.yahoo.com"), or original href if invalid
 */
export function getDisplayDomainFromUrl(href: string): string {
  const domain = getDomainFromUrl(href);
  return domain ?? href;
}

/**
 * Extracts a display-friendly site name from a URL for use in link chips and tooltips.
 * Uses the first domain part (e.g. "cnn" from "cnn.com") and formats it:
 * - Short names (≤4 chars): all caps (e.g. "CNN", "BBC")
 * - Longer names: title case (e.g. "Finance" from "finance.yahoo.com")
 *
 * @param href - Full URL string (e.g. "https://www.cnn.com/world")
 * @returns Formatted site name (e.g. "CNN"), or original href if domain cannot be parsed
 */
export function getSiteNameFromUrl(href: string): string {
  const domain = getDomainFromUrl(href);
  if (!domain) return href;

  const parts = domain.split('.');
  const primaryName = parts[0] || '';
  if (!primaryName) return domain;

  if (primaryName.length <= 4) {
    return primaryName.toUpperCase();
  }
  return primaryName.charAt(0).toUpperCase() + primaryName.slice(1).toLowerCase();
}
